<template>
  <div class="component-wrapper">
    <!-- 查询表单区域 -->
    <div class="query-form-container">
      <div class="query-form-row">
        <div class="form-item">
          <label class="form-label">产品名称</label>
          <a-input v-model:value="formModel.productName" placeholder="请输入产品名称" clearable />
        </div>
        <div class="form-item">
          <label class="form-label">厂商名称</label>
          <a-input v-model:value="formModel.companyName" placeholder="请输入厂商名称" clearable />
        </div>
        <div class="form-item">
          <label class="form-label">所属行业</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.industry" :options="industryOptions"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
        <div class="form-item">
          <label class="form-label">所属行业细分领域</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.specificCategory" :options="specificCategoryOptions"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? `全选 ${checked}/${total}` : `已选 ${checked}/${total}`" />
        </div>
      </div>

      <div class="query-form-row">
        <div class="form-item">
          <label class="form-label">测评类型</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.certificationType" :options="certificationTypeOptions"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
        <div class="form-item">
          <label class="form-label">操作系统类型</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.systemType" :options="systemTypeOptions"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
        <div class="form-item">
          <label class="form-label">测评通过时间</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.approvalTime" :options="yearList"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
      </div>

      <div class="query-button-row">
        <div class="query-button-center">
          <a-button type="primary" @click="handleQuery">查询</a-button>
          <a-button @click="handleReset" style="margin-left: 8px;">重置</a-button>
        </div>
        <div class="query-button-right">
          <a-button :loading="exportLoading" @click="handleExportExcel">
            <template #icon>
              <DownloadOutlined />
            </template>
            导出 Excel
          </a-button>
        </div>
      </div>
    </div>

    <!-- 表格区域：改用表格内置排序事件，避免冲突 -->
    <div class="table-wrapper">
      <a-table :columns="tableColumns" :data-source="tableData" :pagination="pagination" bordered size="middle"
        :loading="tableLoading" :scroll="{ x: 'max-content' }" table-layout="fixed" :row-key="getRowKey"
        @change="handleTableChange">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'industry'">
            {{ record.industry ?? '-' }}
          </template>
          <template v-else-if="column.key === 'specificCategory'">
            {{ record.specificCategory ?? '-' }}
          </template>
          <template v-else-if="column.key === 'certificationType'">
            {{ record.certificationType ?? '-' }}
          </template>
          <template v-else-if="column.key === 'softwareVersion'">
            <span class="software-version-cell">{{ record.softwareVersion ?? '-' }}</span>
          </template>
          <template v-else-if="column.key === 'approvalTime'">
            {{ formatApprovalDate(record.approvalTime) }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import { DownloadOutlined } from '@ant-design/icons-vue';
import MyDropdownCheckbox from '../components/MyDropdownCheckbox.vue';

// 年份列表
const yearList = ref(['2021', '2022', '2023', '2024', '2025', '2026']);

// 选项常量
const industryAndSpecificCategoryList = {
  '安平': ['安防检测设备', '安平监控摄像头', '执法记录仪'],
  '电信': ['路由器', '网关', '网络通信设备'],
  '电子设备': ['笔记本电脑', '空调', '平板电脑', '其他电子设备', '商用电子设备', '消费电子设备', '智能穿戴设备', '智能终端设备'],
  '广电': ['机顶盒', '显示设备'],
  '能源': ['电力监控设备', '燃气表', '消费电子设备', '智能水表'],
  '政务': ['商用电子设备', '信创PC', '政务智能设备', '智能门禁', '自助服务终端'],
  '制造': ['工控机', '工业控制设备', '可编程控制器', '其他制造设备', '制造用模组', '智能制造设备']
};
const industryOptions = [...Object.keys(industryAndSpecificCategoryList)];
const specificCategoryOptions = reactive([]);
const certificationTypeOptions = ['设备', '模组/开发板', '芯片', 'OSV'];
const systemTypeOptions = ['轻量系统', '小型系统', '标准系统'];

// classificationOptions and certificationTypeSelectOptions removed — table is non-editable.

const getSpecificCategoriesByIndustries = (industries) => {
  const categories = industries.flatMap(industry => industryAndSpecificCategoryList[industry] || []);
  const uniqueCategories = [...new Set(categories)];

  return uniqueCategories;
};

// 表单模型
const formModel = reactive({
  productName: '',
  companyName: '',
  industry: [...industryOptions],
  specificCategory: [...specificCategoryOptions],
  certificationType: [...certificationTypeOptions],
  systemType: [...systemTypeOptions],
  approvalTime: [...yearList.value]
});

watch(() => formModel.industry, (newIndustries) => {
  const uniqueCategories = getSpecificCategoriesByIndustries(newIndustries);
  specificCategoryOptions.splice(0, specificCategoryOptions.length, ...uniqueCategories);

  // 保持已选项的选中状态，新增项默认选中
  formModel.specificCategory = formModel.specificCategory.filter(item => uniqueCategories.includes(item));
  uniqueCategories.forEach(item => {
    if (!formModel.specificCategory.includes(item)) {
      formModel.specificCategory.push(item);
    }
  });
}, { immediate: true });

// 分页配置
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 表格加载状态
const tableLoading = ref(false);
const exportLoading = ref(false);

// 表格数据
const tableData = ref([]);

// Inline editing removed — no per-row updating state needed

// 表格列配置：动态绑定排序状态，确保图标和逻辑一致
const tableColumns = computed(() => [
  { title: '产品名称', dataIndex: 'productName', key: 'productName', width: 160 },
  { title: '厂商名称', dataIndex: 'companyName', key: 'companyName', width: 160 },
  { title: '所属行业', dataIndex: 'industry', key: 'industry', width: 110 },
  { title: '所属行业细分领域', dataIndex: 'specificCategory', key: 'specificCategory', width: 130 },
  { title: '测评类型', dataIndex: 'certificationType', key: 'certificationType', width: 110 },
  { title: '设备型号', dataIndex: 'deviceModel', key: 'deviceModel', width: 120 },
  { title: '芯片型号', dataIndex: 'chipModel', key: 'chipModel', width: 120 },
  { title: '操作系统类型', dataIndex: 'systemType', key: 'systemType', width: 120 },
  { title: '操作系统版本号', dataIndex: 'systemVersion', key: 'systemVersion', width: 160 },
  { title: '软件版本号', dataIndex: 'softwareVersion', key: 'softwareVersion', width: 120 },
  {
    title: '测评通过时间',
    dataIndex: 'approvalTime',
    key: 'approvalTime',
    width: 120
  }
]);

const getRowKey = (record) => record.__rowIndex__ ?? `${record.productName}__${record.companyName}__${record.deviceModel}__${Math.random()}`;

const formatApprovalDate = (value) => {
  if (!value) {
    return '-';
  }
  return String(value).slice(0, 10);
};

// displayClassificationSelection and getClassificationValue removed (no inline cascader displayRender needed).

const validateQueryFilters = () => {
  const emptyFields = [
    { label: '所属行业', value: formModel.industry },
    { label: '所属行业细分领域', value: formModel.specificCategory },
    { label: '测评类型', value: formModel.certificationType },
    { label: '操作系统类型', value: formModel.systemType },
    { label: '测评通过时间', value: formModel.approvalTime }
  ]
    .filter(({ value }) => !Array.isArray(value) || value.length === 0)
    .map(({ label }) => label);

  if (emptyFields.length === 0) {
    return true;
  }

  notification.warning({
    message: '查询条件不完整',
    description: `${emptyFields.join('、')}不能为空，请至少选择一项后再查询。`
  });
  return false;
};

const getRequestErrorDescription = (error, fallback = '请稍后重试') => {
  const status = error?.status ?? error?.response?.status;
  if (status === 403) {
    return '您没有权限执行此操作';
  }

  return error?.response?.data?.desc || error?.response?.data?.msg || error?.message || fallback;
};

// Inline edit/update removed — no per-row update API calls from table cells.

// 重置表单
const handleReset = () => {
  const resetSpecificCategories = getSpecificCategoriesByIndustries(industryOptions);

  formModel.productName = '';
  formModel.companyName = '';
  formModel.industry = [...industryOptions];
  formModel.specificCategory = [...resetSpecificCategories];
  formModel.certificationType = [...certificationTypeOptions];
  formModel.systemType = [...systemTypeOptions];
  formModel.approvalTime = [...yearList.value];
};

// 核心：统一的表格数据查询方法（确保排序参数正确传递）
const fetchTableData = async () => {
  if (!validateQueryFilters()) {
    return;
  }

  try {
    tableLoading.value = true;
    const requestBody = {
      productName: formModel.productName,
      companyName: formModel.companyName,
      industry: formModel.industry.length === industryOptions.length ? [] : formModel.industry,
      specificCategory: formModel.specificCategory.length === specificCategoryOptions.length ? [] : formModel.specificCategory,
      certificationType: formModel.certificationType.length === certificationTypeOptions.length ? [] : formModel.certificationType,
      systemType: formModel.systemType.length === systemTypeOptions.length ? [] : formModel.systemType,
      approvalTime: formModel.approvalTime.length === yearList.value.length ? [] : formModel.approvalTime,
      pageVO: {
        curPage: pagination.current,
        pageSize: pagination.pageSize
      }
    };
    const res = await axios.post('/contributorController/getOpProductDeveloperInfoList', requestBody);

    if (res.data?.baseResult?.code !== 200) {
      throw new Error(res.data.desc || '查询失败');
    }
    // 更新表格数据和分页信息
    // 为每条记录添加唯一索引，确保row key不重复
    const newData = (res.data.baseResult.data || []).map((item, index) => ({
      ...item,
      __rowIndex__: `${pagination.current}_${index}_${Date.now()}`
    }));
    tableData.value = newData;
    Object.assign(pagination, {
      pageSize: res.data?.pageVO?.pageSize || pagination.pageSize,
      current: res.data?.pageVO?.curPage || pagination.current,
      total: res.data?.pageVO?.totalRows || 0
    });
  } catch (error) {
    console.error('查询失败：', error);
    notification.error({
      message: '查询失败',
      description: getRequestErrorDescription(error, '请稍后再试')
    });
  } finally {
    tableLoading.value = false;
  }
};

// 手动查询按钮触发
const handleQuery = () => {
  if (!validateQueryFilters()) {
    return;
  }

  pagination.current = 1;
  fetchTableData();
};

const parseFilename = (contentDisposition) => {
  if (!contentDisposition) return 'Product Developers Stat.xlsx';

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const normalMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (normalMatch?.[1]) {
    return decodeURIComponent(normalMatch[1]);
  }

  return 'Product Developers Stat.xlsx';
};

const handleExportExcel = async () => {
  try {
    exportLoading.value = true;
    const requestBody = {
      productName: formModel.productName,
      companyName: formModel.companyName,
      industry: formModel.industry.length === industryOptions.length ? [] : formModel.industry,
      specificCategory: formModel.specificCategory.length === specificCategoryOptions.length ? [] : formModel.specificCategory,
      certificationType: formModel.certificationType.length === certificationTypeOptions.length ? [] : formModel.certificationType,
      systemType: formModel.systemType.length === systemTypeOptions.length ? [] : formModel.systemType,
      approvalTime: formModel.approvalTime.length === yearList.value.length ? [] : formModel.approvalTime
    };
    const res = await axios.post(
      '/contributorController/getExcelOpProductDeveloperInfoList', requestBody, { responseType: 'blob' }
    );

    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = parseFilename(res.headers?.['content-disposition']);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(fileUrl);
  } catch (error) {
    notification.error({
      message: '导出失败',
      description: getRequestErrorDescription(error)
    });
  } finally {
    exportLoading.value = false;
  }
};

const handleTableChange = (pager) => {
  pagination.current = pager?.current || 1;
  pagination.pageSize = pager?.pageSize || pagination.pageSize;
  fetchTableData();
};

onMounted(() => {
  handleQuery();
});

</script>

<style scoped>
.component-wrapper {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.query-form-container {
  width: 100%;
  text-align: left;
}

.query-form-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  width: 100%;
  align-items: center;
  padding-bottom: 8px;
  overflow: visible;
  justify-content: start;
}

.form-item {
  min-width: 0;
  display: flex;
  align-items: center;
}

.form-label {
  font-size: 14px;
  color: #333;
  margin-right: 8px;
  width: 120px;
  flex-shrink: 0;
  white-space: nowrap;
}

.form-item :deep(.ant-input),
.form-item :deep(.ant-select) {
  width: 100% !important;
}

.query-button-row {
  position: relative;
  margin-top: 16px;
  min-height: 32px;
}

.query-button-center {
  display: flex;
  justify-content: center;
}

.query-button-right {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.table-wrapper {
  margin-top: 24px;
  width: 100%;
  overflow-x: auto;
}

.software-version-cell {
  display: inline-block;
  width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-all;
  line-height: 1.4;
}
</style>